import { SB } from '@play-co/replicant';

// Store all friendship requests data
export const friendshipRequest = SB.object({
  // Who created request?
  // It's important because you could store request from other user in your state that waiting your approve
  owner: SB.object({
    id: SB.string(),
    name: SB.string(),
    profilePicture: SB.string(),
    frame: SB.string().optional(),
    species: SB.string(),
  }),
  // Target user data
  target: SB.object({
    id: SB.string(),
    name: SB.string(),
    profilePicture: SB.string(),
    frame: SB.string().optional(),
    species: SB.string(),
  }),
  // pending - request is created but not accepted or rejected
  // accepted - request is accepted ans users became friends
  // rejected - request is rejected, and we will block any other requests from this user for 30 days(auto clear interval)
  status: SB.tuple(['pending', 'accepted', 'rejected']).default('pending'),
  // Request creation timestamp
  createdAt: SB.number(),
  // Last status-change timestamp (created/accepted/rejected)
  updatedAt: SB.number(),
  /**
   * Legacy attribution: which surface initiated this request.
   * Free-form string to stay forward-compatible with new surfaces.
   * Today: one of `friendRequestSourceFeatures` (suggestions, search,
   * profile, visit, visitors, add_friend) — see `friends.analytics.schema.ts`.
   */
  sourceFeature: SB.string().optional(),
  /**
   * Origin attribution — top-level feature that triggered the action.
   * Free-form string. Today: 'visit' or 'friends'. Mirrored onto
   * receiver-side analytics events on discovery / accept / reject.
   */
  originFeature: SB.string().optional(),
  /**
   * Origin attribution — specific surface within `originFeature`.
   * Free-form string, prefixed by `<originFeature>_`. Today:
   * 'visit_visitors', 'visit_suggestions', 'visit_profile',
   * 'friends_suggestions', 'friends_search'.
   */
  originSubFeature: SB.string().optional(),
  /**
   * How the request was created. Free-form string for forward compat;
   * `getFriendshipRequestMethod` narrows to the analytics enum and falls
   * back to `'unknown'` for missing or unrecognised values.
   *
   * Only the in-game-OA send path (`createFriendshipRequest`) writes this
   * field today, and it always sets `'request'`. Share-channel values
   * (`line_share`, `native_share`, `qr_code`) describe how the receiver
   * arrived and never flow through stored state — they are derived from
   * `entryData.subFeature` at receiver-side analytics emission via
   * `getFriendshipShareMethod` / `deriveFriendAddAttribution`.
   */
  method: SB.string().optional(),
  /**
   * Timestamp when the receiver first saw the request (`FriendRequestReceive`
   * fired). `0` means unseen; gates once-only emission in
   * `trackReceivedFriendshipRequests`.
   */
  receivedAt: SB.number().optional(),
  /**
   * `0` once the receiver accepted, marking a request whose sender-side
   * `FriendAdd` is still to be emitted; `trackAcceptedFriendshipRequests`
   * deletes the request when it emits it. The receiver deletes their copy on
   * accept, so an accepted request only exists on the sender's side.
   */
  senderAcknowledgedAt: SB.number().optional(),
});

// In-app friend data (replaced LINE social friends)
const buruFriend = SB.object({
  addedAt: SB.number(),
  /**
   * Friend marked as unfriended if timestamped
   */
  unfriendedAt: SB.number().optional(),
});

/**
 * Log interaction with friends.
 * @deprecated No longer used. Interaction tracking has been removed.
 */
const interaction = SB.object({
  gift: SB.int().optional(),
  visit: SB.int().optional(),
  invite: SB.int().optional(),
  duo: SB.int().optional(),
  continue: SB.int().optional(),
  /** @deprecated Rescue quest feature has been removed */
  rescueQuest: SB.int().optional(),
  /** @deprecated Fairy Forest feature has been removed */
  fairyForest: SB.int().optional(),
  /** @deprecated Happiness booster feature was removed */
  happinessBooster: SB.int().optional(),
  /**
   * This user in a friend list
   */
  isFriend: SB.boolean().optional(),
});

// Export types
export type FriendshipRequestType = SB.ExtractType<typeof friendshipRequest>;

// Export main schema
export default {
  // In-app friends system (replaced LINE social friends)
  buruFriends: SB.map(buruFriend),

  // List of friends that user has initiated unfriend action against
  friendsBlacklist: SB.array(SB.string()).default([]),

  // Map friends requests by target user ID for owner
  // Map friends requests by owner user ID for target user
  friendshipRequests: SB.map(friendshipRequest),

  /**
   * Interaction with friends.
   * @deprecated No longer used. Interaction tracking has been removed.
   */
  friendsInteraction: SB.map(interaction).optional(),
};
